import { AppConfig } from '@/config/appConfig';
import { getAppType } from '@/lib/offline/OfflineAppHelper';
import { useCommonStore } from '@/store/commonStore';
import { calculateExperiencePermissions } from './experiencePermissions';

interface ResourceContextProps {
  resource: string | undefined;
  action: any;
  params: any;
  currentUser?: any;
}

export const calculateResourcePermissions = ({
  resource,
  action,
  params,
  currentUser,
}: ResourceContextProps) => {
  const appType = getAppType();
  const embedded = useCommonStore.getState().embedded;
  const partnerName = useCommonStore.getState().partnerName;
  const currentUserOrg = currentUser?.org;
  const orgType = currentUserOrg?.org_type;
  const enabledFeatures = currentUserOrg?.enabled_features ?? {};
  const isDistrictOrg = currentUserOrg?.org_type === 'district';
  const environment = AppConfig.mode;

  let hasResourcePermission = true;
  let reason = '';
  // Handle experience tab states
  if (resource === 'experience') {
    const { experience, status } = params;
    if (experience) {
      const experiencePermissions = calculateExperiencePermissions({
        experience,
        enabledFeatures,
        currentUser,
        embedded,
      });

      const actionPermission = experiencePermissions[action as keyof typeof experiencePermissions];

      hasResourcePermission = actionPermission[status as keyof typeof actionPermission] as boolean;
      reason = actionPermission.reason;
    }
  }

  if (resource === 'groups') {
    const { group } = params;
    // TODO: Shikhar org_programme_is_mb is either false or null at BE, org_programme_is_mb is not reliable in v2 and v3 both
    const orgProgrammeIsMB = group?.org_programme_is_mb;
    const isArchived = group?.archived;

    // NOTE: Update and delete are has similar logic, so we are handling them together
    if (isArchived && action !== 'delete') {
      hasResourcePermission = false;
    } else if (action === 'update') {
      if (orgProgrammeIsMB) {
        hasResourcePermission = false;
      }
    } else if (action === 'delete') {
      if (orgProgrammeIsMB) {
        hasResourcePermission = false;
      }
    }
  } else if (resource === 'users') {
    const { user } = params;
    const orgProgrammeIsMB = user?.org_programme_is_mb;
    const isArchived = user?.archived;
    const itemUserRole = user?.role;
    const currentUserRole = currentUser?.role;
    const isSuperAdmin = currentUserRole === 'superadmin';
    const isItemRoleQbAuthor = itemUserRole === 'qb_author';

    // NOTE: Update and delete are has similar logic, so we are handling them together

    if (isArchived && action !== 'delete') {
      hasResourcePermission = false;
    } else if (action === 'update') {
      if (orgProgrammeIsMB || (isSuperAdmin && !isItemRoleQbAuthor)) {
        hasResourcePermission = false;
      }
    } else if (action === 'delete') {
      if (orgProgrammeIsMB || isSuperAdmin) {
        hasResourcePermission = false;
      }
    } else if (action === 'update_accommodations') {
      if (!enabledFeatures.student_accommodations || itemUserRole !== 'student') {
        hasResourcePermission = false;
      }
    } else if (action === 'update_password') {
      if (
        !isDistrictOrg &&
        itemUserRole !== 'student' &&
        itemUserRole !== 'parent' // Clear with @shikhar for district org - update_password?
      ) {
        hasResourcePermission = false;
      }
    } else if (action === 'send_welcome_email_to_user') {
      // Adding this action in accessControl.ts as not coming from BE for admin
      if (!enabledFeatures.send_welcome_email_to_user || orgProgrammeIsMB) {
        hasResourcePermission = false;
      }
    }
  } else if (resource === 'gradebook-v3') {
    // APL-10806 — gated behind the `gradebook_v3` feature flag, separate from
    // `experience_analytics` (which gates only the single-assessment analytics
    // tab). Orgs on `gradebook_v3` see v3 in place of the legacy menu entry.
    if (!enabledFeatures?.gradebook_v3 && environment !== 'development') {
      hasResourcePermission = false;
    }
  } else if (resource === 'student_growths') {
    // APL-11550 — District QB author only, dark-gated behind the
    // `student_growth` flag (dev env bypasses the flag). The narrowing +
    // dark switch can't live in the static BE baseline, so it stays here.
    hasResourcePermission = currentUser?.role === 'qb_author' && orgType === 'district';
    if (!enabledFeatures?.student_growth && environment !== 'development') {
      hasResourcePermission = false;
    }
  } else if (resource === 'download_apps') {
    if (enabledFeatures.disable_lockdown_mode || partnerName === '21k' || appType !== 'web') {
      hasResourcePermission = false;
    }
  } else if (resource === 'helps') {
    if (appType !== 'web') {
      hasResourcePermission = false;
    }
  } else if (resource === 'profile') {
    if (orgType === 'district') {
      hasResourcePermission = false;
    }
  }

  return { hasResourcePermission, reason };
};
